import {
  ChangeEventHandler,
  ForwardedRef,
  forwardRef,
  useCallback,
} from "react";
import { Input, InputProps } from "@chakra-ui/react";

function stripNonDigits(raw: string) {
  return raw.replace(/\D/g, "");
}

const formats: Record<number, RegExp> = {
  8: /^(\d{4})?(\d{4})?$/,
  12: /^(\d)?(\d{5})?(\d{5})?(\d)?$/,
  13: /^(\d)?(\d{6})?(\d{6})?$/,
  14: /^(\d)?(\d{2})?(\d{5})?(\d{5})?(\d)?$/,
};

export function formatUpc(upc: string) {
  const normalizedUpc = stripNonDigits(upc);
  const { length } = normalizedUpc;
  const regex = formats[length];
  if (!regex) return upc;

  const matches = normalizedUpc.match(regex);

  if (!matches) return upc;

  return matches
    .slice(1)
    .filter((m) => m)
    .join("-");
}

type Props = Omit<InputProps, "value" | "onChange"> & {
  onChange?: (value: string) => void;
  value?: string | null;
};

// eslint-disable-next-line react/display-name
function UpcInput(
  { onChange, value, autoComplete = "off", ...rest }: Props,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const { value: raw } = e.target;
      const normalized = stripNonDigits(raw).substr(0, 14);
      onChange?.(normalized);
    },
    [onChange],
  );
  return (
    <Input
      ref={ref}
      value={value ? formatUpc(value) : ""}
      onChange={handleChange}
      // Disable autocomplete by default for these since sometimes password
      // managers like to think they can fill formatted inputs
      autoComplete={autoComplete}
      data-1p-ignore={autoComplete === "off" || undefined}
      data-lpignore={autoComplete === "off" || undefined}
      fontFamily="mono"
      fontWeight={300}
      {...rest}
    />
  );
}

export default forwardRef(UpcInput);
